import { useEffect, useState } from "react";
import { Info, Loader2, Mail } from "lucide-react";
import { errorMessage, getSenders, type EmailSender, type EmailSenders } from "@/api/email";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const senderLabel = (sender: EmailSender) => sender.email ?? `Lemlist sender ${sender.user_id}`;
/** A Lemlist user can hold several mailboxes: an option is the pair. */
const senderKey = (sender: EmailSender) => `${sender.user_id}|${sender.email ?? ""}`;

/** "Sent from": the Lemlist sender (and its mailbox) of a campaign. Listed
 *  from Lemlist when emails really go out; in a dry run nothing is asked and
 *  the team's only sender is used at the first sync. */
export function SenderField({
  slug,
  value,
  onChange,
  disabled,
}: {
  slug: string;
  value: EmailSender | null | undefined;
  onChange: (sender: EmailSender | null) => void;
  disabled?: boolean;
}) {
  const [data, setData] = useState<EmailSenders | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getSenders(slug)
      .then((result) => !cancelled && setData(result))
      .catch((err) => !cancelled && setError(errorMessage(err, "The senders could not be read.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // One sender in the team: it is the choice, made explicit.
  useEffect(() => {
    if (!value && data?.external && data.senders.length === 1) {
      const [only] = data.senders;
      onChange({ user_id: only.user_id, email: only.email });
    }
  }, [data, value, onChange]);

  const options = data?.senders ?? [];
  const missing = value && !options.some((s) => senderKey(s) === senderKey(value)) ? value : null;
  // Several mailboxes of one user: its campaign count says nothing about each.
  const shared = (sender: EmailSender) => options.filter((s) => s.user_id === sender.user_id).length > 1;

  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
        <Mail className="h-3.5 w-3.5 text-slate-400" />
        Sent from
      </Label>
      {loading ? (
        <p className="flex h-9 items-center gap-2 text-xs text-slate-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Reading the Lemlist senders…
        </p>
      ) : data && !data.external ? (
        <p className="flex items-start gap-2 rounded-lg bg-white px-3 py-2 text-xs leading-relaxed text-slate-500 ring-1 ring-slate-200">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          Dry run: the mailbox is chosen among your Lemlist senders once emails really go out. Until then, the
          team&apos;s only sender is used at the first sync.
        </p>
      ) : (
        <>
          <Select
            value={value ? senderKey(value) : ""}
            disabled={disabled || (!options.length && !missing)}
            onValueChange={(key) => {
              const picked = options.find((s) => senderKey(s) === key) ?? missing;
              onChange(picked ? { user_id: picked.user_id, email: picked.email } : null);
            }}
          >
            <SelectTrigger className="h-9 w-full bg-white" aria-label="Sending mailbox">
              <SelectValue placeholder={options.length ? "Choose the sending mailbox" : "No sender in Lemlist"} />
            </SelectTrigger>
            <SelectContent>
              {options.map((sender) => (
                <SelectItem key={senderKey(sender)} value={senderKey(sender)}>
                  <span className="font-medium text-slate-800">{senderLabel(sender)}</span>
                  {!shared(sender) && (
                    <span className="ml-2 text-xs text-slate-400">
                      {sender.campaigns ? `${sender.campaigns} Lemlist campaign${sender.campaigns === 1 ? "" : "s"}` : "no campaign yet"}
                    </span>
                  )}
                </SelectItem>
              ))}
              {missing && (
                <SelectItem value={senderKey(missing)}>
                  <span className="font-medium text-slate-800">{senderLabel(missing)}</span>
                  <span className="ml-2 text-xs text-amber-600">not listed by Lemlist anymore</span>
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <p className="text-[11px] leading-snug text-slate-400">
            {error ??
              data?.note ??
              "Every email of this campaign, follow-ups included, leaves from this mailbox. Lemlist's API cannot pick a mailbox: if Lemlist uses another one, the sync says so and the campaign waits until you choose this one in Lemlist (Settings › Senders)."}
          </p>
        </>
      )}
    </div>
  );
}

/** The campaign header's "Sent from" line. Without a chosen sender it names
 *  the team's only one, or says that one must be chosen before a sync. */
export function SentFromLine({
  slug,
  sender,
  canManage,
}: {
  slug: string;
  sender: EmailSender | null | undefined;
  canManage: boolean;
}) {
  const [team, setTeam] = useState<EmailSenders | null>(null);

  useEffect(() => {
    if (sender || !canManage) return;
    let cancelled = false;
    getSenders(slug)
      .then((result) => !cancelled && setTeam(result))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sender, canManage, slug]);

  const listed = !sender && team?.external ? team.senders : [];
  const only = listed.length === 1 ? listed[0] : null;

  return (
    <p className="flex flex-wrap items-center gap-x-1.5 text-sm text-slate-500">
      <Mail className="h-3.5 w-3.5 text-slate-400" />
      {listed.length > 1 ? (
        <span className="text-amber-700">No sending mailbox chosen: pick one in Settings before the next sync.</span>
      ) : (
        <>
          Sent from
          <span className="font-medium text-slate-700">
            {sender ? senderLabel(sender) : only ? senderLabel(only) : "the team's Lemlist sender"}
          </span>
          {only && <span className="text-xs text-slate-400">· the team&apos;s only sender</span>}
        </>
      )}
    </p>
  );
}
